import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading } from "../../components/VB";
import RupiahInput from "../../components/RupiahInput";
import { toast } from "sonner";
import { Plus, Trash2, HandCoins, CheckCircle2 } from "lucide-react";

const fmt = (n) => "Rp " + (n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

export default function Kasbon() {
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const nextMonth = new Date(); nextMonth.setMonth(nextMonth.getMonth() + 1);
  const defaultMulai = nextMonth.toISOString().slice(0, 7);

  const [form, setForm] = useState({
    employee_id: "", tanggal_pinjam: today, nominal_total: 0,
    tenor_bulan: 1, tanggal_mulai_potong: defaultMulai, catatan: ""
  });
  const [del, setDel] = useState(null);

  const perBulan = form.nominal_total && form.tenor_bulan ? form.nominal_total / form.tenor_bulan : 0;

  const load = async () => {
    setLoading(true);
    try {
      const [k, e] = await Promise.all([api.get("/kasbon"), api.get("/employees")]);
      setItems(k.data); setEmps(e.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/kasbon", {
        ...form,
        nominal_total: parseFloat(form.nominal_total),
        tenor_bulan: parseInt(form.tenor_bulan),
      });
      toast.success("Kasbon dicatat & arus kas keluar tercatat");
      setOpen(false);
      setForm({
        employee_id: "", tanggal_pinjam: today, nominal_total: 0,
        tenor_bulan: 1, tanggal_mulai_potong: defaultMulai, catatan: ""
      });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/kasbon/${del.id}`); toast.success("Kasbon dihapus"); setDel(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const totalAktif = items.filter((k) => k.status === "Aktif").reduce((a, k) => a + (k.nominal_sisa || 0), 0);

  return (
    <Window
      title="Kasbon Karyawan (Pinjaman)"
      actions={<button className="vb-btn" onClick={() => setOpen(true)} data-testid="add-kasbon"><Plus className="w-3 h-3 inline mr-1" />Beri Kasbon</button>}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginBottom: 12 }}>
        <div className="vb-statcard">
          <div className="vb-statcard-label">Total Kasbon Aktif</div>
          <div className="vb-statcard-value" style={{ color: "#003399", fontSize: 16 }}>{items.filter((k) => k.status === "Aktif").length}</div>
        </div>
        <div className="vb-statcard">
          <div className="vb-statcard-label">Sisa Belum Tertagih</div>
          <div className="vb-statcard-value" style={{ color: "#d8000c", fontSize: 18 }}>{fmt(totalAktif)}</div>
        </div>
        <div className="vb-statcard">
          <div className="vb-statcard-label">Total Kasbon Lunas</div>
          <div className="vb-statcard-value" style={{ color: "#4f8a10", fontSize: 16 }}>{items.filter((k) => k.status === "Lunas").length}</div>
        </div>
      </div>

      <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, fontSize: 11, marginBottom: 10 }}>
        ℹ <strong>Cara Kerja Kasbon:</strong> Tentukan nominal total + tenor (1-3 bulan). Sistem akan otomatis hitung cicilan per bulan dan memotong dari gaji karyawan saat payroll diproses pada bulan yang ditentukan.
      </div>

      {loading ? <Loading /> : items.length === 0 ? <EmptyState message="Belum ada kasbon" /> : (
        <div style={{ overflowX: "auto" }}>
          <table className="vb-table">
            <thead>
              <tr>
                <th>Karyawan</th><th>Tgl Pinjam</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th>Tenor</th>
                <th style={{ textAlign: "right" }}>Per Bulan</th>
                <th>Mulai Potong</th>
                <th style={{ textAlign: "right" }}>Sudah Dipotong</th>
                <th style={{ textAlign: "right" }}>Sisa</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td>{it.employee_name} <span style={{ fontSize: 10, color: "#666" }}>({it.no_karyawan})</span></td>
                  <td>{it.tanggal_pinjam}</td>
                  <td style={{ textAlign: "right" }}>{fmt(it.nominal_total)}</td>
                  <td style={{ textAlign: "center" }}>{it.tenor_bulan} bln</td>
                  <td style={{ textAlign: "right" }}>{fmt(it.nominal_per_bulan)}</td>
                  <td>{it.tanggal_mulai_potong}</td>
                  <td style={{ textAlign: "right", color: "#4f8a10" }}>{fmt(it.nominal_dibayar)}</td>
                  <td style={{ textAlign: "right", color: "#d8000c", fontWeight: 600 }}>{fmt(it.nominal_sisa)}</td>
                  <td>
                    {it.status === "Lunas" ? (
                      <span className="vb-badge vb-badge-approved"><CheckCircle2 className="w-3 h-3 inline mr-1" />Lunas</span>
                    ) : (
                      <span className="vb-badge vb-badge-izin">Aktif ({it.sisa_bulan} bln)</span>
                    )}
                  </td>
                  <td>
                    {it.nominal_dibayar === 0 && (
                      <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)} data-testid={`kasbon-del-${it.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={open} onClose={() => setOpen(false)} title="Beri Kasbon ke Karyawan" width={500}
        footer={<>
          <button className="vb-btn" onClick={() => setOpen(false)}>Batal</button>
          <button className="vb-btn-primary" onClick={submit} data-testid="kasbon-save">
            <HandCoins className="w-3 h-3 inline mr-1" />Berikan Kasbon
          </button>
        </>}
      >
        <form onSubmit={submit}>
          <div className="vb-form-row">
            <label className="vb-label">Karyawan*</label>
            <select className="vb-select" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} required data-testid="kasbon-employee">
              <option value="">- pilih karyawan -</option>
              {emps.map((e) => <option key={e.id} value={e.id}>{e.nama_lengkap} ({e.no_karyawan})</option>)}
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Tanggal Pinjam*</label>
            <input type="date" className="vb-input" value={form.tanggal_pinjam} onChange={(e) => setForm({ ...form, tanggal_pinjam: e.target.value })} required data-testid="kasbon-tanggal" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Nominal Total Kasbon*</label>
            <RupiahInput value={form.nominal_total} onChange={(v) => setForm({ ...form, nominal_total: v })} required testId="kasbon-nominal" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Tenor (Lama Cicilan)*</label>
            <select className="vb-select" value={form.tenor_bulan} onChange={(e) => setForm({ ...form, tenor_bulan: parseInt(e.target.value) })} required data-testid="kasbon-tenor">
              <option value={1}>1 Bulan (potong sekaligus)</option>
              <option value={2}>2 Bulan</option>
              <option value={3}>3 Bulan</option>
              <option value={4}>4 Bulan</option>
              <option value={5}>5 Bulan</option>
              <option value={6}>6 Bulan</option>
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Mulai Potong (Periode Gaji)*</label>
            <input type="month" className="vb-input" value={form.tanggal_mulai_potong} onChange={(e) => setForm({ ...form, tanggal_mulai_potong: e.target.value })} required data-testid="kasbon-mulai" />
          </div>

          {/* Preview cicilan */}
          {form.nominal_total > 0 && form.tenor_bulan > 0 && (
            <div style={{ background: "#e8f4ff", border: "1px solid #c4d9f1", padding: 10, fontSize: 12, marginBottom: 8 }}>
              <strong>📊 Preview Cicilan:</strong><br />
              Cicilan per bulan: <strong style={{ color: "#003399", fontSize: 14 }}>{fmt(perBulan)}</strong> × {form.tenor_bulan} bulan<br />
              Total potongan = {fmt(perBulan * form.tenor_bulan)}
            </div>
          )}

          <div className="vb-form-row">
            <label className="vb-label">Catatan</label>
            <textarea rows={2} className="vb-textarea" value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} placeholder="Alasan / keperluan kasbon" />
          </div>
          <div style={{ fontSize: 11, color: "#9F6000", padding: 6, background: "#FEEFB3", border: "1px solid #9F6000", marginTop: 4 }}>
            ⚠ Saat kasbon diberikan, Rp {fmt(form.nominal_total).replace("Rp ", "")} akan tercatat sebagai pengeluaran kas (kategori "Kasbon Karyawan"). Cicilan akan otomatis dipotong dari gaji.
          </div>
        </form>
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus kasbon ${del?.employee_name}?`} />
    </Window>
  );
}
